import { useState, useCallback } from 'react';

export function useFormValidation<T extends Record<string, any>>() {
  const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof T, string>>>({});

  const setError = useCallback((field: keyof T, error: string) => {
    setValidationErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const clearError = useCallback((field: keyof T) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  const hasErrors = Object.keys(validationErrors).length > 0;

  const validateFields = useCallback((
    data: T, 
    rules: Partial<Record<keyof T, (value: any) => string | undefined>>
  ): boolean => {
    const errors: Partial<Record<keyof T, string>> = {};

    Object.entries(rules).forEach(([field, validator]) => {
      if (validator) {
        const error = validator(data[field as keyof T]);
        if (error) {
          errors[field as keyof T] = error;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  return {
    validationErrors,
    setError,
    clearError,
    clearAllErrors,
    hasErrors,
    validateFields
  };
} 